import { type ComponentProps, type PropsWithChildren, type ReactNode, type RefObject, useRef, useState } from 'react';

import { MenuItem } from '@class-ui/react-components';
import { MoreHoriz } from '@mui/icons-material';
import { Button, IconButton, ListItemIcon, ListItemText, Menu, Tooltip } from '@mui/material';
import { type Theme } from '@mui/material/styles';
import { type SxProps } from '@mui/system';
import styled from 'styled-components';

const StyledButton = styled(Button)`
    min-width: fit-content;
`;

export type ActionOnClick = (uniqueId: string, buttonRef?: RefObject<unknown>) => void | Promise<void>;

export type ActionMenuItem = {
    name: string;
    onClick: ActionOnClick;
    keepOpen?: boolean;
    icon?: ReactNode;
    menuItemProps?: ComponentProps<typeof MenuItem>;
};

export type ActionMenuProps = PropsWithChildren<{
    uniqueId: string;
    title: string;
    actions: ActionMenuItem[];
    sx?: SxProps<Theme>;
    component?: 'button' | 'icon-button';
}>;

export const ActionMenu = (props: ActionMenuProps) => {
    const { uniqueId, title, actions, sx, component = 'button', children } = props;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const buttonRef = useRef<HTMLButtonElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleAction = (action: ActionOnClick, keepOpen = false) => {
        if (!keepOpen) {
            setAnchorEl(null);
        }

        action(uniqueId, buttonRef);
    };

    return (
        <>
            {component === 'button' && (
                <StyledButton
                    variant="outlined"
                    aria-label="more"
                    id={`action-button-${uniqueId}`}
                    aria-controls={open ? `action-menu-${uniqueId}` : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-haspopup="true"
                    onClick={handleClick}
                    title={title}
                    ref={buttonRef}
                    sx={sx}
                >
                    {children || <MoreHoriz color="primary" fontSize="large" />}
                </StyledButton>
            )}

            {component === 'icon-button' && (
                <Tooltip title={title}>
                    <IconButton
                        aria-label={title}
                        id={`action-button-${uniqueId}`}
                        aria-controls={open ? `action-menu-${uniqueId}` : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="true"
                        onClick={handleClick}
                        ref={buttonRef}
                        sx={sx}
                    >
                        {children || <MoreHoriz />}
                    </IconButton>
                </Tooltip>
            )}
            <Menu
                id={`action-menu-${uniqueId}`}
                MenuListProps={{
                    'aria-labelledby': `action-button-${uniqueId}`
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                role="listbox"
            >
                {actions.map((action, index) => (
                    <MenuItem
                        key={`${uniqueId}-${index}`}
                        onClick={() => handleAction(action.onClick, action.keepOpen ?? false)}
                        {...action.menuItemProps}
                    >
                        {Boolean(action.icon) && <ListItemIcon>{action.icon}</ListItemIcon>}
                        <ListItemText>{action.name}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};
